import { postStatusStyle, whenLabel, type Post } from '@/types/posts'
import { todayIso } from '@/shared/util/dates'

/** How many cards a phone shows before it says what it is holding back. */
const CAP = 12
import { ChannelDots } from './ChannelDots'
import './MobilePostCards.css'

/**
 * The publishing plan on a phone.
 *
 * Not the table narrowed — a different view of the same data, and the design is
 * specific about how different. It shows the posts falling on **the next three
 * dates that have any**, capped at eight cards, and says so when there are
 * none: "No posts scheduled in the next few days."
 *
 * That is a decision about what a phone is for. Sitting at a desk you audit the
 * whole plan; standing somewhere with a phone you are checking what goes out
 * soon and who owns it. Three dates rather than three days, so a plan with a
 * quiet week still shows you something.
 *
 * ## Searching escapes the window
 *
 * The search box stays on mobile, and the near-term filter would make it lie: a
 * post from last month matches, and nothing appears. So an active search or
 * filter shows the matches instead, with the count stated. The design does not
 * cover this case — a prototype's search is always over its own fixture data —
 * and it is flagged in designfeedback.md rather than decided quietly.
 */
export function MobilePostCards({
  rows,
  narrowed,
  onEdit,
}: {
  rows: Post[]
  /** True when a search, Mine or status filter is on. */
  narrowed: boolean
  onEdit: (p: Post) => void
}) {
  const today = todayIso()

  /**
   * What comes next, and then everything undated.
   *
   * This used to show only "the next three dates that actually have posts",
   * which silently dropped every post without one. Since Idea became the first
   * status, **undated is the normal state** — so on a phone the whole plan read
   * as empty while the header above it said "2 posts · 2 shown". A list that
   * disagrees with its own count is worse than a long list.
   */
  const dated = rows
    .filter((p) => p.date && p.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time))
  const past = rows
    .filter((p) => p.date && p.date < today)
    .sort((a, b) => b.date.localeCompare(a.date))
  const undated = rows.filter((p) => !p.date)

  /**
   * Three named groups, capped, with the scope said out loud — decision 42, and
   * decision 43 behind it: a list that filters by date **groups the undated,
   * never drops them**, and **states its own scope when it truncates**.
   *
   * The labels are the part that was missing rather than the order. Unlabelled,
   * an undated post directly under a dated one reads as a late one.
   */
  const groups = narrowed
    ? [{ label: '', items: rows }]
    : [
        { label: 'Coming up', items: dated },
        { label: 'No date yet', items: undated },
        { label: 'Past', items: past },
      ].filter((g) => g.items.length > 0)

  const total = groups.reduce((n, g) => n + g.items.length, 0)
  // Capped across the whole list, then sliced group by group, so the cap never
  // silently removes a whole group's heading along with its rows.
  let budget = CAP
  const capped = groups
    .map((g) => {
      const items = g.items.slice(0, Math.max(0, budget))
      budget -= items.length
      return { ...g, items }
    })
    .filter((g) => g.items.length > 0)
  const shown = capped.flatMap((g) => g.items)
  const hidden = total - shown.length

  if (shown.length === 0) {
    return (
      <div className="empty-note">
        {narrowed ? 'No posts match these filters.' : 'No posts in the plan yet.'}
      </div>
    )
  }

  return (
    <div className="postcards">
      {/* Never a silent cap: a list that stops without saying so reads as a
          complete answer. The remedy is named, because "open it on a computer"
          is not one when the phone is what somebody has. */}
      <p className="postcards__scope">
        {hidden > 0
          ? `${shown.length} of ${total} shown. Filter by status or channel to narrow the plan.`
          : `${total} post${total === 1 ? '' : 's'}`}
      </p>

      {capped.flatMap((g) => [
        g.label ? (
          <p className="postcards__group" key={`g-${g.label}`}>
            {g.label}
          </p>
        ) : null,
        ...g.items.map((p) => renderCard(p)),
      ])}
    </div>
  )

  /** One post as a card. Declared after the return, hoisted, read once. */
  function renderCard(p: Post) {
    const st = postStatusStyle(p.status)
    return (
          <button className="postcard" key={p.id} onClick={() => onEdit(p)}>
            <div className="postcard__top">
              {/* The subject, now that posts carry one — two cards both reading
                  "Post · No date yet · Bjarte Olsen" were indistinguishable,
                  which the 15 Aug test found on a phone. The content type is
                  the fallback for posts written before subjects existed. */}
              <span className="postcard__title">{p.subject || p.type}</span>
              <span className="pill" style={{ background: st.bg, color: st.color }}>
                {st.label}
              </span>
            </div>

            <div className="postcard__when">
              {whenLabel(p)}
            </div>

            <div className="postcard__channels">
              <ChannelDots channels={p.channels} />
            </div>

      <div className="postcard__owner">
          Owner: <strong>{p.owner || 'unassigned'}</strong>
        </div>
      </button>
    )
  }
}
